import { useState } from "react";
import { useTablist } from "@/hooks/use-tablist";
import { DoctorLayout } from "@/components/layout/doctor-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

function ScoreBar({ score, bands }: { score: number; bands: { label: string; max: number; colour: string }[] }) {
  const maxScore = bands[bands.length - 1].max;
  const pct = Math.min((score / maxScore) * 100, 100);
  const band = bands.find((b) => score <= b.max) ?? bands[bands.length - 1];
  return (
    <div className="mt-3">
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>Score: <strong className="text-slate-900 text-sm">{score.toFixed(2)}</strong></span>
        <span className={`font-semibold ${band.colour}`}>{band.label}</span>
      </div>
      <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-indigo-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function NumberInput({ label, value, onChange, min = 0, max, step = 1 }: {
  label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </div>
  );
}

// DAS28-CRP calculator
function DAS28CRPCalc() {
  const [tjc, setTjc] = useState(0);
  const [sjc, setSjc] = useState(0);
  const [crp, setCrp] = useState(0);
  const [gh, setGh] = useState(50);

  const das28 = 0.56 * Math.sqrt(tjc) + 0.28 * Math.sqrt(sjc) + 0.36 * Math.log(crp + 1) + 0.014 * gh + 0.96;

  const bands = [
    { label: "Remission", max: 2.4, colour: "text-emerald-600" },
    { label: "Low", max: 2.9, colour: "text-teal-600" },
    { label: "Moderate", max: 5.1, colour: "text-amber-600" },
    { label: "High", max: 100, colour: "text-red-600" },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">DAS28-CRP</CardTitle>
          <div className="text-xs text-slate-500 space-x-2">
            <Badge variant="outline">RA</Badge>
            <Badge variant="outline">NICE biologic threshold: ≥5.1</Badge>
          </div>
        </div>
        <p className="text-xs text-slate-500">0.56√TJC28 + 0.28√SJC28 + 0.36·ln(CRP+1) + 0.014·GH + 0.96</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <NumberInput label="Tender Joint Count (TJC28)" value={tjc} onChange={setTjc} min={0} max={28} />
          <NumberInput label="Swollen Joint Count (SJC28)" value={sjc} onChange={setSjc} min={0} max={28} />
          <NumberInput label="CRP (mg/L)" value={crp} onChange={setCrp} min={0} step={0.1} />
          <NumberInput label="Global Health (VAS 0–100)" value={gh} onChange={setGh} min={0} max={100} />
        </div>
        <ScoreBar score={das28} bands={bands} />
        <p className="text-xs text-slate-500">Remission &lt;2.4 · Low &lt;2.9 · Moderate ≤5.1 · High &gt;5.1</p>
      </CardContent>
    </Card>
  );
}

// DAS28-ESR calculator
function DAS28ESRCalc() {
  const [tjc, setTjc] = useState(0);
  const [sjc, setSjc] = useState(0);
  const [esr, setEsr] = useState(10);
  const [gh, setGh] = useState(50);

  const das28 = 0.56 * Math.sqrt(tjc) + 0.28 * Math.sqrt(sjc) + 0.7 * Math.log(esr) + 0.014 * gh;

  const bands = [
    { label: "Remission", max: 2.6, colour: "text-emerald-600" },
    { label: "Low", max: 3.2, colour: "text-teal-600" },
    { label: "Moderate", max: 5.1, colour: "text-amber-600" },
    { label: "High", max: 100, colour: "text-red-600" },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">DAS28-ESR</CardTitle>
          <Badge variant="outline">RA</Badge>
        </div>
        <p className="text-xs text-slate-500">0.56√TJC28 + 0.28√SJC28 + 0.7·ln(ESR) + 0.014·GH</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <NumberInput label="Tender Joint Count (TJC28)" value={tjc} onChange={setTjc} min={0} max={28} />
          <NumberInput label="Swollen Joint Count (SJC28)" value={sjc} onChange={setSjc} min={0} max={28} />
          <NumberInput label="ESR (mm/hr)" value={esr} onChange={setEsr} min={1} />
          <NumberInput label="Global Health (VAS 0–100)" value={gh} onChange={setGh} min={0} max={100} />
        </div>
        <ScoreBar score={das28} bands={bands} />
        <p className="text-xs text-slate-500">Remission &lt;2.6 · Low ≤3.2 · Moderate ≤5.1 · High &gt;5.1</p>
      </CardContent>
    </Card>
  );
}

// CDAI calculator
function CDAICalc() {
  const [tjc, setTjc] = useState(0);
  const [sjc, setSjc] = useState(0);
  const [pga, setPga] = useState(0);
  const [ega, setEga] = useState(0);

  const cdai = tjc + sjc + pga + ega;

  const bands = [
    { label: "Remission", max: 2.8, colour: "text-emerald-600" },
    { label: "Low", max: 10, colour: "text-teal-600" },
    { label: "Moderate", max: 22, colour: "text-amber-600" },
    { label: "High", max: 76, colour: "text-red-600" },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">CDAI</CardTitle>
          <div className="text-xs text-slate-500 space-x-2">
            <Badge variant="outline">RA</Badge>
          </div>
        </div>
        <p className="text-xs text-slate-500">TJC28 + SJC28 + PGA(0–10) + EGA(0–10) — no lab required</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <NumberInput label="Tender Joint Count (TJC28)" value={tjc} onChange={setTjc} min={0} max={28} />
          <NumberInput label="Swollen Joint Count (SJC28)" value={sjc} onChange={setSjc} min={0} max={28} />
          <NumberInput label="Patient Global Assessment (0–10 cm)" value={pga} onChange={setPga} min={0} max={10} step={0.1} />
          <NumberInput label="Evaluator Global Assessment (0–10 cm)" value={ega} onChange={setEga} min={0} max={10} step={0.1} />
        </div>
        <ScoreBar score={cdai} bands={bands} />
        <p className="text-xs text-slate-500">Remission ≤2.8 · Low ≤10 · Moderate ≤22 · High &gt;22</p>
      </CardContent>
    </Card>
  );
}

// SDAI calculator
function SDAICalc() {
  const [tjc, setTjc] = useState(0);
  const [sjc, setSjc] = useState(0);
  const [pga, setPga] = useState(0);
  const [ega, setEga] = useState(0);
  const [crp, setCrp] = useState(0);

  const sdai = tjc + sjc + pga + ega + crp;

  const bands = [
    { label: "Remission", max: 3.3, colour: "text-emerald-600" },
    { label: "Low", max: 11, colour: "text-teal-600" },
    { label: "Moderate", max: 26, colour: "text-amber-600" },
    { label: "High", max: 86, colour: "text-red-600" },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">SDAI</CardTitle>
          <Badge variant="outline">RA</Badge>
        </div>
        <p className="text-xs text-slate-500">TJC28 + SJC28 + PGA(0–10) + EGA(0–10) + CRP(mg/dL)</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <NumberInput label="Tender Joint Count (TJC28)" value={tjc} onChange={setTjc} min={0} max={28} />
          <NumberInput label="Swollen Joint Count (SJC28)" value={sjc} onChange={setSjc} min={0} max={28} />
          <NumberInput label="Patient Global Assessment (0–10 cm)" value={pga} onChange={setPga} min={0} max={10} step={0.1} />
          <NumberInput label="Evaluator Global Assessment (0–10 cm)" value={ega} onChange={setEga} min={0} max={10} step={0.1} />
          <NumberInput label="CRP (mg/dL) — note: mg/dL not mg/L" value={crp} onChange={setCrp} min={0} step={0.1} />
        </div>
        <ScoreBar score={sdai} bands={bands} />
        <p className="text-xs text-slate-500">Remission ≤3.3 · Low ≤11 · Moderate ≤26 · High &gt;26</p>
      </CardContent>
    </Card>
  );
}

// SLEDAI-2K calculator
function SLEDAI2KCalc() {
  const items = [
    { label: "Seizure", weight: 8 },
    { label: "Psychosis", weight: 8 },
    { label: "Organic brain syndrome", weight: 8 },
    { label: "Visual disturbance", weight: 8 },
    { label: "Cranial nerve disorder", weight: 8 },
    { label: "Lupus headache (severe, persistent)", weight: 8 },
    { label: "CVA (not due to arteriosclerosis)", weight: 8 },
    { label: "Vasculitis", weight: 8 },
    { label: "Arthritis (≥2 joints with signs of inflammation)", weight: 4 },
    { label: "Myositis", weight: 4 },
    { label: "Urinary casts", weight: 4 },
    { label: "Haematuria (>5 RBC/hpf, excluding stones/infection)", weight: 4 },
    { label: "Proteinuria (>0.5g/24h)", weight: 4 },
    { label: "Pyuria (>5 WBC/hpf, excluding infection)", weight: 4 },
    { label: "New rash", weight: 2 },
    { label: "Alopecia (new/recurrent, abnormal patchy or diffuse)", weight: 2 },
    { label: "Mucosal ulcers (new/recurrent)", weight: 2 },
    { label: "Pleuritis", weight: 2 },
    { label: "Pericarditis", weight: 2 },
    { label: "Low complement (CH50, C3, or C4 < normal)", weight: 2 },
    { label: "Increased DNA binding (>25% binding by Farr assay)", weight: 2 },
    { label: "Fever (>38°C after exclusion of infection)", weight: 1 },
    { label: "Thrombocytopenia (<100,000/mm³)", weight: 1 },
    { label: "Leucopenia (<3,000/mm³, after drug exclusion)", weight: 1 },
  ];

  const [checked, setChecked] = useState<Set<number>>(new Set());
  const score = [...checked].reduce((s, i) => s + items[i].weight, 0);

  const toggle = (i: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const bands = [
    { label: "No activity", max: 0, colour: "text-emerald-600" },
    { label: "Mild", max: 6, colour: "text-teal-600" },
    { label: "Moderate", max: 12, colour: "text-amber-600" },
    { label: "High", max: 105, colour: "text-red-600" },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">SLEDAI-2K</CardTitle>
          <Badge variant="outline">SLE</Badge>
        </div>
        <p className="text-xs text-slate-500">Tick all features present in the last 10 days</p>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
          {items.map((item, i) => (
            <label key={i} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded-lg px-2 py-1">
              <input
                type="checkbox"
                checked={checked.has(i)}
                onChange={() => toggle(i)}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-slate-700 flex-1">{item.label}</span>
              <span className="text-xs font-semibold text-indigo-600">+{item.weight}</span>
            </label>
          ))}
        </div>
        <ScoreBar score={score} bands={bands} />
        <p className="text-xs text-slate-500">Total: {score} · Mild ≤6 · Moderate ≤12 · High &gt;12</p>
      </CardContent>
    </Card>
  );
}

// BASDAI calculator
function BASDAICalc() {
  const [q1, setQ1] = useState(0);
  const [q2, setQ2] = useState(0);
  const [q3, setQ3] = useState(0);
  const [q4, setQ4] = useState(0);
  const [q5, setQ5] = useState(0);
  const [q6, setQ6] = useState(0);

  const basdai = ((q1 + q2 + q3 + q4 + (q5 + q6) / 2) / 5);

  const bands = [
    { label: "Well controlled", max: 3.9, colour: "text-emerald-600" },
    { label: "Active disease", max: 10, colour: "text-red-600" },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">BASDAI</CardTitle>
          <div className="text-xs text-slate-500 space-x-2">
            <Badge variant="outline">axSpA / AS</Badge>
            <Badge variant="outline">NICE threshold: ≥4</Badge>
          </div>
        </div>
        <p className="text-xs text-slate-500">All answers on 0–10 NRS. BASDAI ≥4 = active disease requiring biologic consideration</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <NumberInput label="Q1: Overall fatigue/tiredness (0–10)" value={q1} onChange={setQ1} min={0} max={10} step={0.1} />
          <NumberInput label="Q2: Overall AS neck/back/hip pain (0–10)" value={q2} onChange={setQ2} min={0} max={10} step={0.1} />
          <NumberInput label="Q3: Overall pain/swelling in joints (0–10)" value={q3} onChange={setQ3} min={0} max={10} step={0.1} />
          <NumberInput label="Q4: Overall discomfort from tender areas (0–10)" value={q4} onChange={setQ4} min={0} max={10} step={0.1} />
          <NumberInput label="Q5: Overall morning stiffness severity (0–10)" value={q5} onChange={setQ5} min={0} max={10} step={0.1} />
          <NumberInput label="Q6: Morning stiffness duration (0–10, 0=none, 10=≥2h)" value={q6} onChange={setQ6} min={0} max={10} step={0.1} />
        </div>
        <ScoreBar score={basdai} bands={bands} />
        <p className="text-xs text-slate-500">Formula: (Q1+Q2+Q3+Q4+mean(Q5,Q6)) ÷ 5. Active disease ≥4.</p>
      </CardContent>
    </Card>
  );
}

// BVAS calculator
function BVASCalc() {
  const systems = [
    {
      name: "General",
      items: [
        { label: "Myalgia/arthralgia/arthritis", weight: 1 },
        { label: "Fever ≥38.5°C", weight: 3 },
        { label: "Weight loss ≥2 kg", weight: 2 },
      ],
    },
    {
      name: "Cutaneous",
      items: [
        { label: "Infarct", weight: 6 },
        { label: "Purpura", weight: 3 },
        { label: "Other skin vasculitis", weight: 3 },
      ],
    },
    {
      name: "Mucous membranes/eyes",
      items: [
        { label: "Mouth ulcers", weight: 3 },
        { label: "Genital ulcers", weight: 1 },
        { label: "Significant proptosis", weight: 3 },
        { label: "Scleritis", weight: 6 },
        { label: "Uveitis", weight: 6 },
        { label: "Sudden visual loss", weight: 9 },
      ],
    },
    {
      name: "ENT",
      items: [
        { label: "Nasal discharge/stuffiness", weight: 2 },
        { label: "Sinus involvement (radiological or clinical)", weight: 2 },
        { label: "Sub-glottic involvement", weight: 6 },
        { label: "Conductive hearing loss", weight: 3 },
        { label: "Sensorineural hearing loss", weight: 6 },
      ],
    },
    {
      name: "Chest",
      items: [
        { label: "Wheeze", weight: 2 },
        { label: "Nodules or cavities (CXR)", weight: 4 },
        { label: "Pleural effusion", weight: 4 },
        { label: "Infiltrate (CXR)", weight: 4 },
        { label: "Haemoptysis/endobronchitis", weight: 4 },
        { label: "Respiratory failure", weight: 9 },
      ],
    },
    {
      name: "Cardiovascular",
      items: [
        { label: "Pulse deficit or loss", weight: 4 },
        { label: "Aortic incompetence/aneurysm", weight: 4 },
        { label: "Cardiac ischaemia/cardiomyopathy", weight: 4 },
        { label: "Pericarditis", weight: 4 },
      ],
    },
    {
      name: "Abdominal",
      items: [
        { label: "Peritonism", weight: 9 },
        { label: "Bloody diarrhoea", weight: 6 },
        { label: "Ischaemic abdominal pain", weight: 6 },
      ],
    },
    {
      name: "Renal",
      items: [
        { label: "Haematuria ≥1+ on urine dipstick", weight: 6 },
        { label: "Proteinuria ≥1+ (>0.2g/24h)", weight: 4 },
        { label: "Creatinine 125–249 µmol/L", weight: 4 },
        { label: "Creatinine 250–499 µmol/L", weight: 6 },
        { label: "Creatinine ≥500 µmol/L or rise ≥30% or 2-fold", weight: 9 },
      ],
    },
    {
      name: "Nervous system",
      items: [
        { label: "Headache", weight: 1 },
        { label: "Meningitis", weight: 9 },
        { label: "Stroke", weight: 9 },
        { label: "Cord lesion", weight: 9 },
        { label: "Cranial nerve palsy", weight: 6 },
        { label: "Sensory peripheral neuropathy", weight: 6 },
        { label: "Motor mononeuritis multiplex", weight: 9 },
      ],
    },
  ];

  const allItems: { label: string; weight: number; sysIdx: number; itemIdx: number }[] = [];
  systems.forEach((sys, si) => sys.items.forEach((item, ii) => allItems.push({ ...item, sysIdx: si, itemIdx: ii })));

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const score = [...checked].reduce((s, key) => {
    const found = allItems.find((it) => `${it.sysIdx}-${it.itemIdx}` === key);
    return s + (found?.weight ?? 0);
  }, 0);

  const toggle = (key: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const bands = [
    { label: "Remission", max: 0, colour: "text-emerald-600" },
    { label: "Low activity", max: 3, colour: "text-teal-600" },
    { label: "Moderate", max: 9, colour: "text-amber-600" },
    { label: "High", max: 200, colour: "text-red-600" },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">BVAS (Birmingham Vasculitis Activity Score)</CardTitle>
          <Badge variant="outline">ANCA Vasculitis / GCA</Badge>
        </div>
        <p className="text-xs text-slate-500">Tick all features present. Persistent features score half weight (clinical scoring tool).</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="max-h-80 overflow-y-auto space-y-3 pr-1">
          {systems.map((sys, si) => (
            <div key={si}>
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">{sys.name}</p>
              <div className="space-y-1">
                {sys.items.map((item, ii) => {
                  const key = `${si}-${ii}`;
                  return (
                    <label key={ii} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded-lg px-2 py-1">
                      <input
                        type="checkbox"
                        checked={checked.has(key)}
                        onChange={() => toggle(key)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-slate-700 flex-1">{item.label}</span>
                      <span className="text-xs font-semibold text-indigo-600">+{item.weight}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <ScoreBar score={score} bands={bands} />
        <p className="text-xs text-slate-500">Score: {score}. Remission = 0. Active disease ≥1. Severe flare typically &gt;15.</p>
      </CardContent>
    </Card>
  );
}

const CALCULATORS = [
  { id: "das28crp", label: "DAS28-CRP", condition: "RA", component: <DAS28CRPCalc /> },
  { id: "das28esr", label: "DAS28-ESR", condition: "RA", component: <DAS28ESRCalc /> },
  { id: "cdai", label: "CDAI", condition: "RA", component: <CDAICalc /> },
  { id: "sdai", label: "SDAI", condition: "RA", component: <SDAICalc /> },
  { id: "sledai", label: "SLEDAI-2K", condition: "SLE", component: <SLEDAI2KCalc /> },
  { id: "basdai", label: "BASDAI", condition: "axSpA", component: <BASDAICalc /> },
  { id: "bvas", label: "BVAS", condition: "Vasculitis", component: <BVASCalc /> },
];

export default function DiseaseActivity() {
  const [active, setActive] = useState("das28crp");
  const calc = CALCULATORS.find((c) => c.id === active)!;
  const { getTabProps } = useTablist({ items: CALCULATORS, activeId: active, onActivate: setActive });

  return (
    <DoctorLayout title="Disease Activity Scores">
      <div className="mb-6">
        <p className="text-slate-600 mb-4">
          Interactive disease activity calculators with treat-to-target thresholds. Scores should be interpreted in the full clinical context.
        </p>
        <div role="tablist" aria-label="Disease activity calculators" className="flex flex-wrap gap-2">
          {CALCULATORS.map((c, i) => (
            <button
              key={c.id}
              {...getTabProps(c.id, i)}
              aria-controls={`calc-panel-${c.id}`}
              id={`calc-tab-${c.id}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                active === c.id
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:text-indigo-600"
              }`}
            >
              {c.label}
              <span className={`ml-1.5 text-xs ${active === c.id ? "text-indigo-200" : "text-slate-400"}`}>
                {c.condition}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div
        id={`calc-panel-${calc.id}`}
        role="tabpanel"
        aria-labelledby={`calc-tab-${calc.id}`}
      >
        {calc.component}
      </div>

      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800">
        <strong>Clinical note:</strong> These calculators are tools to support treat-to-target monitoring. Scores must be interpreted alongside clinical examination, patient reported outcomes, and imaging findings. NICE biologic prescribing thresholds (e.g. DAS28 &gt;5.1, BASDAI ≥4) are eligibility criteria — individual prescribing decisions require consultant assessment.
      </div>
    </DoctorLayout>
  );
}
